import {
  CheckOutlined,
  CloseOutlined,
  CodeOutlined,
  CompassOutlined,
  EditOutlined,
  MoreOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Flex,
  Input,
  type InputRef,
  type MenuProps,
  Switch,
  Tooltip,
  Typography,
} from "@p0security/ui";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { colors } from "../../styles/variables";

export type EditorMode = "advanced" | "guided";

const HEADER_CLASS = "bg-white pt-4 pb-3 sticky -top-4 md:-top-6 z-10";

const TITLE_CLASS =
  "cursor-pointer m-0 overflow-hidden text-ellipsis whitespace-nowrap";

const nameDisplayClass = (isEditing: boolean): string =>
  `rounded-md max-w-full min-w-0 px-3 py-1 ${
    isEditing ? "bg-transparent" : colors.neutral["05"]
  }`;

export const EditorHeader: React.FC<{
  name: string;
  disabled: boolean;
  isDirty: boolean;
  isValid: boolean;
  isSubmitting?: boolean;
  onNameChange: (name: string) => void;
  onDisabledChange: (disabled: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  namePlaceholder?: string;
  untitledLabel?: string;
  saveLabel?: string;
  enabledTooltip?: string;
  mode?: EditorMode;
  onModeChange?: (value: EditorMode) => void;
  saveIcon?: React.ReactNode;
  saveTooltip?: string;
  saveAdornment?: React.ReactNode;
}> = ({
  name,
  disabled,
  isDirty,
  isValid,
  isSubmitting = false,
  onNameChange,
  onDisabledChange,
  onSave,
  onCancel,
  namePlaceholder = "Name (optional)",
  untitledLabel = "Untitled",
  saveLabel = "Save",
  enabledTooltip = "Toggle enabled state.",
  mode,
  onModeChange,
  saveIcon = <SaveOutlined />,
  saveTooltip,
  saveAdornment,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<InputRef>(null);
  const showModeMenu = mode !== undefined && onModeChange !== undefined;

  const handleEditClick = useCallback(() => {
    setDraft(name);
    setIsEditingName(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [name]);

  const handleDraftChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value);
    },
    []
  );

  const handleConfirmName = useCallback(() => {
    onNameChange(draft);
    setIsEditingName(false);
  }, [draft, onNameChange]);

  const handleCancelName = useCallback(() => {
    setIsEditingName(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleConfirmName();
      if (e.key === "Escape") handleCancelName();
    },
    [handleConfirmName, handleCancelName]
  );

  const preventBlur = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleEnabledChange = useCallback(
    (checked: boolean) => {
      onDisabledChange(!checked);
    },
    [onDisabledChange]
  );

  const modeMenu = useMemo<MenuProps>(
    () => ({
      items: [
        {
          icon: <CompassOutlined />,
          key: "guided" satisfies EditorMode,
          label: "Guided editor",
        },
        {
          icon: <CodeOutlined />,
          key: "advanced" satisfies EditorMode,
          label: "YAML editor",
        },
      ],
      onClick: ({ key }) => onModeChange?.(key as EditorMode),
      selectable: true,
      selectedKeys: mode ? [mode] : [],
    }),
    [mode, onModeChange]
  );

  return (
    <div className={HEADER_CLASS}>
      <Flex
        align="center"
        justify="space-between"
        gap={16}
        wrap
        className="min-w-0"
      >
        <Flex align="center" gap={12} className="min-w-0 flex-auto">
          <Flex
            align="center"
            gap={8}
            className={nameDisplayClass(isEditingName)}
          >
            {isEditingName ? (
              <>
                <Input
                  ref={inputRef}
                  value={draft}
                  onChange={handleDraftChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleConfirmName}
                  placeholder={namePlaceholder}
                  size="large"
                  className="w-100"
                />
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={handleConfirmName}
                />
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onMouseDown={preventBlur}
                  onClick={handleCancelName}
                />
              </>
            ) : (
              <>
                <Typography.Title
                  level={4}
                  className={TITLE_CLASS}
                  type={name ? undefined : "secondary"}
                  onClick={handleEditClick}
                >
                  {name || untitledLabel}
                </Typography.Title>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={handleEditClick}
                />
              </>
            )}
          </Flex>
          <Tooltip title={enabledTooltip}>
            <Switch
              checked={!disabled}
              onChange={handleEnabledChange}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Tooltip>
        </Flex>
        <Flex align="center" gap={8} flex="0 0 auto">
          {isDirty && (
            <Typography.Text type="secondary" className="whitespace-nowrap">
              Unsaved changes
            </Typography.Text>
          )}
          <Button icon={<CloseOutlined />} onClick={onCancel}>
            Cancel
          </Button>
          <Tooltip title={!isValid && saveTooltip ? saveTooltip : ""}>
            <Button
              type="primary"
              icon={saveIcon}
              onClick={onSave}
              disabled={!isValid || isSubmitting || !isDirty}
              loading={isSubmitting}
            >
              {saveLabel}
              {saveAdornment}
            </Button>
          </Tooltip>
          {showModeMenu && (
            <Dropdown
              menu={modeMenu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                aria-label="Editor options"
              />
            </Dropdown>
          )}
        </Flex>
      </Flex>
    </div>
  );
};
